import { supabase } from '@/lib/supabaseClient';
import type { User } from '@supabase/supabase-js';
import type { Profile } from '@/types/auth';

export interface ProfilePatch {
  full_name?: string;
  phone?: string;
  city?: string;
  country?: string;
}

// Debug visible si ?qa=1 ou DEV
const showDebug = typeof window !== 'undefined' && 
  (new URLSearchParams(window.location.search).get('qa') === '1' || import.meta.env.DEV);

/**
 * Get or create profile for a user - VERSION ALIGNÉE SUR LE VRAI SCHÉMA
 * SCHÉMA RÉEL : user_id (PK) = auth.users.id
 * - Si le profil existe déjà -> le retourner
 * - Si le profil n'existe pas -> le créer avec user_id = auth.users.id
 * - Si insert échoue à cause d'un doublon -> refaire un select et retourner le profil existant
 */
export async function getOrCreateProfile(user: User): Promise<Profile> {
  try {
    if (showDebug) console.log('🔍 [PROFILE] getOrCreateProfile pour:', user.email, 'user_id:', user.id);

    // 1) Essayer de récupérer le profil existant par user_id (clé primaire réelle)
    const { data: existing, error: selectError } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (selectError) {
      console.error('❌ [PROFILE] Erreur select profil:', selectError.message);
      throw new Error(`Erreur lors de la récupération du profil: ${selectError.message}`);
    }

    // 2) Si le profil existe, le retourner (avec update email si nécessaire)
    if (existing) {
      if (showDebug) console.log('✅ [PROFILE] Profil existant trouvé:', { user_id: existing.user_id, role: existing.role, status: existing.status });

      // Update email si manquant
      if (!existing.email && user.email) {
        const { data: updated, error: updateError } = await supabase
          .from('profiles')
          .update({
            email: user.email,
            updated_at: new Date().toISOString(),
          })
          .eq('user_id', user.id)
          .select()
          .single();

        if (updateError) {
          console.warn('⚠️ [PROFILE] Impossible de mettre à jour l\'email du profil:', updateError.message);
          return existing as Profile;
        }

        if (showDebug) console.log('✅ [PROFILE] Email mis à jour');
        return updated as Profile;
      }

      return existing as Profile;
    }

    // 3) Le profil n'existe pas, essayer de le créer avec user_id = auth.users.id
    if (showDebug) console.log('⚠️ [PROFILE] Profil inexistant, tentative création avec user_id:', user.id);

    const { data: created, error: insertError } = await supabase
      .from('profiles')
      .insert({
        user_id: user.id, // Clé primaire = auth.users.id
        email: user.email ?? null,
        status: 'NEW',
        role: 'COMMERCE', // Valeur par défaut du schéma
      })
      .select()
      .single();

    // 4) Si l'insert réussit, retourner le profil créé
    if (!insertError && created) {
      if (showDebug) console.log('✅ [PROFILE] Profil créé avec succès, user_id:', created.user_id);
      return created as Profile;
    }

    // 5) Si l'insert échoue à cause d'un doublon (race condition), refaire un select
    if (insertError) {
      const isDuplicate = 
        insertError.message.includes('duplicate') || 
        insertError.message.includes('already exists') ||
        insertError.code === '23505'; // PostgreSQL unique violation

      if (isDuplicate) {
        if (showDebug) console.log('⚠️ [PROFILE] Conflit détecté (doublon), nouvelle tentative select par user_id...');

        // Refaire un select pour récupérer le profil qui existe maintenant
        const { data: retryExisting, error: retryError } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (retryError) {
          console.error('❌ [PROFILE] Erreur retry select:', retryError.message);
          throw new Error(`Erreur lors de la récupération du profil après conflit: ${retryError.message}`);
        }

        if (retryExisting) {
          if (showDebug) console.log('✅ [PROFILE] Profil récupéré après conflit, user_id:', retryExisting.user_id);
          return retryExisting as Profile;
        }

        // Si toujours pas de profil, erreur critique
        throw new Error('Impossible de récupérer le profil après conflit de création');
      }

      // Autre erreur d'insertion
      console.error('❌ [PROFILE] Erreur insert profil:', insertError.message);
      throw new Error(`Erreur lors de la création du profil: ${insertError.message}`);
    }

    // Cas improbable : pas d'erreur mais pas de data
    throw new Error('Création du profil échouée sans erreur explicite');

  } catch (err) {
    const message = err instanceof Error ? err.message : 'Erreur inconnue';
    console.error('❌ [PROFILE] getOrCreateProfile exception:', message);
    throw new Error(`getOrCreateProfile: ${message}`);
  }
}

/**
 * Update profile fields (full_name, phone, city, country)
 * Utilise user_id comme clé primaire
 */
export async function updateProfile(userId: string, patch: ProfilePatch): Promise<Profile> {
  try {
    if (showDebug) console.log('🔍 [PROFILE] updateProfile pour userId:', userId, 'patch:', patch);

    const { data, error } = await supabase
      .from('profiles')
      .update({
        ...patch,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', userId) // Utiliser user_id comme clé primaire
      .select()
      .single();

    if (error) {
      console.error('❌ [PROFILE] Erreur update profil:', error.message);
      throw new Error(`Impossible de mettre à jour votre profil`);
    }

    if (!data) {
      throw new Error('Aucune donnée retournée après la mise à jour');
    }

    if (showDebug) console.log('✅ [PROFILE] Profil mis à jour avec succès');
    return data as Profile;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Erreur inconnue';
    console.error('❌ [PROFILE] updateProfile exception:', message);
    throw new Error(message);
  }
}

/**
 * Set user role (admin only)
 * Utilise user_id comme clé primaire
 */
export async function setRole(userId: string, role: 'AO' | 'COMMERCE' | 'ADMIN'): Promise<void> {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({
        role,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', userId); // Utiliser user_id comme clé primaire

    if (error) {
      throw new Error(`Erreur lors de la mise à jour du rôle: ${error.message}`);
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Erreur inconnue';
    throw new Error(`setRole: ${message}`);
  }
}

/**
 * Set user status (admin only)
 * Utilise user_id comme clé primaire
 */
export async function setStatus(
  userId: string,
  status: 'NEW' | 'FORM_SUBMITTED' | 'VALIDATED' | 'REJECTED'
): Promise<void> {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({
        status,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', userId); // Utiliser user_id comme clé primaire

    if (error) {
      throw new Error(`Erreur lors de la mise à jour du statut: ${error.message}`);
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Erreur inconnue';
    throw new Error(`setStatus: ${message}`);
  }
}

/**
 * Ensure user is admin (karlcibois@gmail.com only)
 * - Insert into admin_users if not exists
 * - Update profiles to role=ADMIN, status=VALIDATED
 * Utilise user_id comme clé primaire
 */
export async function ensureAdmin(user: User): Promise<void> {
  try {
    // Only for karlcibois@gmail.com
    if (user.email !== 'karlcibois@gmail.com') {
      return;
    }

    if (showDebug) console.log('🔍 [PROFILE] ensureAdmin pour Karl, user_id:', user.id);

    // S'assurer qu'un profil existe d'abord
    await getOrCreateProfile(user);

    // Insert into admin_users (ON CONFLICT DO NOTHING via upsert)
    const { error: adminError } = await supabase
      .from('admin_users')
      .upsert(
        {
          user_id: user.id,
          email: user.email,
          level: 'superadmin',
        },
        { onConflict: 'user_id' }
      );

    if (adminError) {
      console.warn('⚠️ [PROFILE] Impossible d\'insérer dans admin_users:', adminError.message);
    } else {
      if (showDebug) console.log('✅ [PROFILE] admin_users mis à jour');
    }

    // Update profiles to ADMIN + VALIDATED (utiliser user_id comme clé primaire)
    const { error: profileError } = await supabase
      .from('profiles')
      .update({
        role: 'ADMIN',
        status: 'VALIDATED',
        email: user.email,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', user.id); // Utiliser user_id comme clé primaire

    if (profileError) {
      console.warn('⚠️ [PROFILE] Impossible de mettre à jour le profil admin:', profileError.message);
    } else {
      if (showDebug) console.log('✅ [PROFILE] Profil mis à jour en ADMIN');
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Erreur inconnue';
    console.error(`❌ [PROFILE] ensureAdmin: ${message}`);
  }
}